import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Scissors, Leaf, Sprout, Truck, Sparkles, Package,
  Check, Calendar as CalIcon, Clock, CheckCircle2, Plus, Users, Wrench, Tag, MapPin,
  Phone, Home, X, CreditCard,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const ICONS = {
  mowing: Sprout, weed_whacking: Scissors, pruning: Leaf, moving: Truck,
  special_request: Sparkles, full_yard: Package,
};

const MOWER_SURCHARGE = 10;
const WHACKER_DISCOUNT = 5;
const ADDON_DISCOUNT = 5;

const WORKER_LABELS = { 1: "Just the owner", 2: "Owner + 1 helper", 3: "Full crew" };
const WORKER_SUBS = { 1: "1 person", 2: "2 people", 3: "3 people" };
const DURATIONS = [
  { min: 30, label: "30 min" }, { min: 60, label: "1 hour" },
  { min: 90, label: "1.5 hours" }, { min: 120, label: "2 hours" },
];

function applySale(price, s) {
  if (price == null || !s?.on_sale) return price;
  if (s.sale_type === "percent") return Math.max(0, Math.round((price * (1 - s.sale_value / 100)) * 100) / 100);
  return Math.max(0, Math.round((price - s.sale_value) * 100) / 100);
}

function prettyDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function computeTotal(service, addonSvcs, duration, workers, mower, whacker) {
  if (!service) return null;
  if (service.kind === "special_request") return null;
  if (service.kind === "moving") {
    if (!duration || !workers) return null;
    const rate = service.worker_options?.find((w) => w.count === workers)?.rate || 25;
    return applySale((duration / 30) * rate, service);
  }
  let total = applySale(service.price ?? 0, service);
  if (service.needs_mower && mower === "need") total += MOWER_SURCHARGE;
  if (service.needs_whacker && whacker === "own") total -= WHACKER_DISCOUNT;
  addonSvcs.forEach((a) => { total += a.on_sale ? (a.addon_sale_price ?? 0) : (a.addon_price ?? 0); });
  return Math.round(total * 100) / 100;
}

const STEP_LABELS = { service: "Service", datetime: "Date & Time", details: "Details", done: "Done" };

export default function Booking() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [service, setService] = useState(null);
  const [addons, setAddons] = useState([]);
  const [duration, setDuration] = useState(null);
  const [workers, setWorkers] = useState(null);
  const [mower, setMower] = useState(null);
  const [whacker, setWhacker] = useState(null);
  const [special, setSpecial] = useState("");
  const [notes, setNotes] = useState("");
  const [preVisit, setPreVisit] = useState(null); // "call" | "visit" | null
  const [selectedDate, setSelectedDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "", address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const flow = useMemo(
    () => (service?.no_appointment ? ["service", "details", "done"] : ["service", "datetime", "details", "done"]),
    [service]
  );
  const stepKey = flow[step] || "service";

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/availability").then((r) => setAvailability(r.data)).catch(() => {});
  }, []);

  const addonOptions = useMemo(() => services.filter((s) => s.is_addon), [services]);
  const addonSvcs = useMemo(() => addonOptions.filter((a) => addons.includes(a.id)), [addonOptions, addons]);
  const total = useMemo(() => computeTotal(service, addonSvcs, duration, workers, mower, whacker),
    [service, addonSvcs, duration, workers, mower, whacker]);
  const timesForDate = useMemo(() => availability.find((a) => a.date === selectedDate)?.times || [], [availability, selectedDate]);

  const pickService = (s) => {
    setService(s); setAddons([]); setDuration(null); setWorkers(null); setMower(null); setWhacker(null);
  };
  const toggleAddon = (id) => setAddons((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const canNext = () => {
    if (stepKey === "service") {
      if (!service) return false;
      if (service.kind === "special_request") return special.trim().length > 3;
      if (service.kind === "moving" && (!duration || !workers)) return false;
      if (service.needs_mower && !mower) return false;
      if (service.needs_whacker && !whacker) return false;
      return true;
    }
    if (stepKey === "datetime") return !!slot;
    if (stepKey === "details") return customer.name && customer.email && customer.phone && customer.address;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", {
        service_id: service.id,
        slot_id: service.no_appointment ? null : slot?.id,
        addons: service.allow_addons ? addons : [],
        duration_minutes: service.kind === "moving" ? duration : null,
        workers: service.kind === "moving" ? workers : null,
        mower: service.needs_mower ? mower : null,
        whacker: service.needs_whacker ? whacker : null,
        notes: notes.trim() || null,
        pre_visit: preVisit,
        special_request: service.kind === "special_request" ? special : null,
        customer,
      });
      setConfirmed(res.data);
      setStep(3);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      if (e.response?.status === 409) {
        api.get("/availability").then((r) => setAvailability(r.data));
        setSlot(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const EquipToggle = ({ testid, active, onClick, title, note, delta }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex-1 rounded border p-4 text-left transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}
    >
      <div className="font-semibold text-sm">{title}</div>
      <div className={`text-xs mt-1 ${active ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{note}</div>
      {delta && <div className={`text-sm mt-2 font-bold ${active ? "text-primary-foreground" : "text-primary"}`}>{delta}</div>}
    </button>
  );

  return (
    <div className="App min-h-screen bg-background">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="flex items-center gap-2 mb-10" data-testid="booking-progress">
          {flow.map((key, i) => (
            <div key={key} className="flex items-center gap-2 flex-1">
              <div className={`grid place-items-center w-8 h-8 rounded-full text-xs font-bold shrink-0 transition-colors ${i <= step ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs font-bold uppercase tracking-wider hidden sm:block ${i <= step ? "text-foreground" : "text-muted-foreground"}`}>{STEP_LABELS[key]}</span>
              {i < flow.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {stepKey === "service" && (
            <motion.div key="s0" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
              <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mb-8">Pick a service</h1>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {services.map((s) => {
                  const Icon = ICONS[s.id] || Sprout;
                  const active = service?.id === s.id;
                  const showPrice = s.on_sale ? s.sale_price : s.price;
                  const priceLabel = s.kind === "moving" ? `from $${showPrice}` : showPrice != null ? `$${showPrice}` : "Quote";
                  return (
                    <button key={s.id} data-testid={`select-service-${s.id}`} onClick={() => pickService(s)}
                      className={`text-left bg-card border rounded p-6 flex gap-4 transition-transform active:scale-[0.99] hover:-translate-y-0.5 ${active ? "border-primary ring-2 ring-primary/30" : "border-border"}`}>
                      <span className={`grid place-items-center w-11 h-11 rounded shrink-0 ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-primary"}`}><Icon className="w-5 h-5" /></span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="font-display font-semibold text-lg">{s.name}</h3>
                          <span className="font-display font-bold text-primary whitespace-nowrap flex items-center gap-1.5">
                            {s.on_sale && s.price != null && <span className="text-xs text-muted-foreground line-through font-normal">${s.price}</span>}
                            {s.kind === "moving" ? `from $${showPrice}` : showPrice != null ? `$${showPrice}` : "Quote"}
                          </span>
                        </div>
                        {s.on_sale && <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wider bg-accent text-accent-foreground px-2 py-0.5 rounded">{s.sale_label}</span>}
                        <p className="text-muted-foreground text-sm mt-1 leading-relaxed">{s.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Equipment: mower */}
              {service?.needs_mower && (
                <div className="mt-6 bg-secondary/60 rounded p-6" data-testid="mower-wrap">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-primary mb-4"><Wrench className="w-4 h-4" /> Do you have a mower?</div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <EquipToggle testid="mower-own" active={mower === "own"} onClick={() => setMower("own")} title="I have my own mower" note="You provide the mower" delta="Included" />
                    <EquipToggle testid="mower-need" active={mower === "need"} onClick={() => setMower("need")} title="Bring a mower" note="I'll bring my own" delta={`+$${MOWER_SURCHARGE}`} />
                  </div>
                </div>
              )}

              {/* Equipment: whacker */}
              {service?.needs_whacker && (
                <div className="mt-6 bg-secondary/60 rounded p-6" data-testid="whacker-wrap">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-primary mb-4"><Wrench className="w-4 h-4" /> Do you have a weed whacker?</div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <EquipToggle testid="whacker-own" active={whacker === "own"} onClick={() => setWhacker("own")} title="I have my own weed whacker" note="You provide the whacker" delta={`Save $${WHACKER_DISCOUNT}`} />
                    <EquipToggle testid="whacker-need" active={whacker === "need"} onClick={() => setWhacker("need")} title="Bring a weed whacker" note="I'll bring my own" delta="Included" />
                  </div>
                </div>
              )}

              {/* Add-ons (mowing) with discount */}
              {service?.allow_addons && addonOptions.length > 0 && (
                <div className="mt-6 bg-secondary/60 rounded p-6" data-testid="addons-wrap">
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-primary mb-4"><Plus className="w-4 h-4" /> Add to your mow &amp; save</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {addonOptions.map((a) => {
                      const on = addons.includes(a.id);
                      const Icon = ICONS[a.id] || Leaf;
                      const orig = (a.addon_price ?? 0) + ADDON_DISCOUNT;
                      return (
                        <button key={a.id} data-testid={`toggle-addon-${a.id}`} onClick={() => toggleAddon(a.id)}
                          className={`relative flex items-center gap-3 rounded border p-4 text-left transition-colors ${on ? "border-primary bg-card" : "border-border bg-card hover:border-primary"}`}>
                          <span className="absolute -top-2 -right-2 inline-flex items-center gap-1 bg-accent text-accent-foreground text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"><Tag className="w-2.5 h-2.5" /> Save ${ADDON_DISCOUNT}</span>
                          <span className={`grid place-items-center w-5 h-5 rounded border ${on ? "bg-primary border-primary text-primary-foreground" : "border-border"}`}>{on && <Check className="w-3.5 h-3.5" />}</span>
                          <Icon className="w-4 h-4 text-primary" />
                          <span className="font-semibold text-sm flex-1">{a.name}</span>
                          <span className="text-right leading-tight">
                            <span className="text-xs text-muted-foreground line-through mr-1.5">${orig}</span>
                            <span className="font-display font-bold text-primary">+${a.addon_price}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Moving crew + duration */}
              {service?.kind === "moving" && (
                <div className="mt-6 bg-secondary/60 rounded p-6 space-y-6" data-testid="duration-wrap">
                  <div>
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-primary mb-4"><Users className="w-4 h-4" /> How many people do you need?</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {(service.worker_options || []).map((w) => (
                        <button key={w.count} data-testid={`select-workers-${w.count}`} onClick={() => setWorkers(w.count)}
                          className={`rounded border p-4 text-left transition-colors ${workers === w.count ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}>
                          <div className="font-display font-bold">{WORKER_LABELS[w.count]}</div>
                          <div className={`text-xs mt-0.5 ${workers === w.count ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{WORKER_SUBS[w.count]}</div>
                          <div className={`text-sm mt-2 font-semibold ${workers === w.count ? "text-primary-foreground" : "text-primary"}`}>${w.rate} / 30 min</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  {workers && (
                    <div>
                      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-primary mb-4"><Clock className="w-4 h-4" /> How long do you need?</div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {DURATIONS.map((d) => {
                          const rate = service.worker_options?.find((w) => w.count === workers)?.rate || 25;
                          return (
                            <button key={d.min} data-testid={`select-duration-${d.min}`} onClick={() => setDuration(d.min)}
                              className={`rounded border p-4 text-center transition-colors ${duration === d.min ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}>
                              <div className="font-display font-bold">{d.label}</div>
                              <div className={`text-sm mt-1 ${duration === d.min ? "text-primary-foreground/80" : "text-primary"}`}>${(d.min / 30) * rate}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Special request */}
              {service?.kind === "special_request" && (
                <div className="mt-6" data-testid="special-request-wrap">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em]">Describe your request</Label>
                  <Textarea data-testid="special-request-input" value={special} onChange={(e) => setSpecial(e.target.value)} placeholder="e.g. Clear leaves from the backyard and haul away 3 bags of debris" className="mt-2 min-h-28" />
                </div>
              )}

              {total != null && (
                <div className="mt-6 flex items-center justify-end gap-3" data-testid="running-total">
                  <span className="text-sm text-muted-foreground">Estimated total</span>
                  <span className="font-display font-black text-2xl text-primary">${total}</span>
                </div>
              )}
            </motion.div>
          )}

          {stepKey === "datetime" && (
            <motion.div key="s1" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }}>
              <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mb-8">Choose a date &amp; time</h1>
              {availability.length === 0 ? (
                <div className="bg-card border border-border rounded p-10 text-center" data-testid="no-availability">
                  <CalIcon className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No available times right now. Please check back soon!</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-4"><CalIcon className="w-4 h-4" /> Available dates</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {availability.map((a) => (
                        <button key={a.date} data-testid={`select-date-${a.date}`} onClick={() => { setSelectedDate(a.date); setSlot(null); }} className={`px-3 py-4 rounded border text-center transition-transform active:scale-95 ${selectedDate === a.date ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary"}`}>
                          <div className="font-display font-bold">{prettyDate(a.date)}</div>
                          <div className={`text-xs mt-1 ${selectedDate === a.date ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{a.times.length} slot{a.times.length !== 1 ? "s" : ""}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-4"><Clock className="w-4 h-4" /> Available times</div>
                    {!selectedDate ? (
                      <p className="text-muted-foreground text-sm">Select a date first.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {timesForDate.map((t) => (
                          <button key={t.id} data-testid={`select-time-${t.id}`} onClick={() => setSlot(t)} className={`px-3 py-3 rounded border font-medium text-sm transition-transform active:scale-95 ${slot?.id === t.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary"}`}>{t.time}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {stepKey === "details" && (
            <motion.div key="s2" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="grid md:grid-cols-12 gap-8">
              <div className="md:col-span-7">
                <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mb-8">Your details</h1>
                <div className="space-y-5">
                  {[
                    { k: "name", label: "Full name", ph: "Jane Doe", type: "text" },
                    { k: "email", label: "Email", ph: "jane@example.com", type: "email" },
                    { k: "phone", label: "Phone", ph: "(555) 123-4567", type: "tel" },
                  ].map((f) => (
                    <div key={f.k}>
                      <Label className="text-xs font-bold uppercase tracking-[0.15em]">{f.label}</Label>
                      <Input data-testid={`customer-${f.k}-input`} type={f.type} value={customer[f.k]} onChange={(e) => setCustomer({ ...customer, [f.k]: e.target.value })} placeholder={f.ph} className="mt-2" />
                    </div>
                  ))}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.15em]">Service address</Label>
                    <Textarea data-testid="customer-address-input" value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} placeholder="123 Maple St, Red Deer, AB" className="mt-2" />
                    <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5" data-testid="service-area-note">
                      <MapPin className="w-3 h-3" /> Currently serving Red Deer, AB only.
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.15em]">Any special requests or details? <span className="text-muted-foreground font-normal normal-case">(optional)</span></Label>
                    <Textarea data-testid="booking-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Gate code, pets in the yard, areas to focus on, anything else I should know..." className="mt-2 min-h-24" />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-[0.15em]">Want to connect before your appointment? <span className="text-muted-foreground font-normal normal-case">(optional)</span></Label>
                    <p className="text-xs text-muted-foreground mt-1 mb-3">I'm happy to set up a quick call or a short visit beforehand so I can plan the best way to do the job for you.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[
                        { v: null, icon: X, title: "No thanks", note: "Just the appointment" },
                        { v: "call", icon: Phone, title: "Quick call", note: "Chat by phone first" },
                        { v: "visit", icon: Home, title: "Quick visit", note: "Stop by beforehand" },
                      ].map((o) => (
                        <button key={String(o.v)} data-testid={`pre-visit-${o.v || "none"}`} onClick={() => setPreVisit(o.v)}
                          className={`rounded border p-4 text-left transition-colors ${preVisit === o.v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary"}`}>
                          <o.icon className="w-4 h-4 mb-1.5" />
                          <div className="font-semibold text-sm">{o.title}</div>
                          <div className={`text-xs mt-0.5 ${preVisit === o.v ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{o.note}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-5">
                <div className="bg-secondary rounded p-6 sticky top-24" data-testid="booking-summary">
                  <h3 className="font-display font-bold text-lg mb-4">Booking summary</h3>
                  <dl className="space-y-3 text-sm">
                    <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Service</dt><dd className="font-semibold text-right">{service?.name}</dd></div>
                    {addonSvcs.length > 0 && (
                      <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Add-ons</dt><dd className="font-semibold text-right">{addonSvcs.map((a) => a.name).join(", ")}</dd></div>
                    )}
                    {service?.needs_mower && mower && (
                      <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Mower</dt><dd className="font-semibold text-right">{mower === "own" ? "I provide" : "Owner brings (+$10)"}</dd></div>
                    )}
                    {service?.needs_whacker && whacker && (
                      <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Whacker</dt><dd className="font-semibold text-right">{whacker === "own" ? "I provide (−$5)" : "Owner brings"}</dd></div>
                    )}
                    {service?.kind === "moving" && duration && (
                      <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Duration</dt><dd className="font-semibold text-right">{DURATIONS.find((d) => d.min === duration)?.label}</dd></div>
                    )}
                    {service?.kind === "moving" && workers && (
                      <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Crew</dt><dd className="font-semibold text-right">{WORKER_SUBS[workers]}</dd></div>
                    )}
                    {service?.kind === "special_request" && special && (
                      <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Request</dt><dd className="font-medium text-right">{special}</dd></div>
                    )}
                    {!service?.no_appointment && (
                      <>
                        <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Date</dt><dd className="font-semibold text-right">{selectedDate ? prettyDate(selectedDate) : "-"}</dd></div>
                        <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Time</dt><dd className="font-semibold text-right">{slot?.time || "-"}</dd></div>
                      </>
                    )}
                    {service?.no_appointment && (
                      <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Scheduling</dt><dd className="font-semibold text-right">I'll reach out to arrange</dd></div>
                    )}
                    <div className="border-t border-border pt-3 flex justify-between gap-4"><dt className="text-muted-foreground">Total</dt><dd className="font-display font-bold text-primary">{total != null ? `$${total}` : "Custom quote"}</dd></div>
                  </dl>
                  <p className="text-xs text-muted-foreground mt-4 leading-relaxed">Payment is collected after the service is completed.</p>
                  <div className="mt-3 rounded bg-card border border-border p-3" data-testid="payment-methods">
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-primary"><CreditCard className="w-3.5 h-3.5" /> Payment methods</div>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                      I accept <span className="font-semibold text-foreground">Cash</span>, <span className="font-semibold text-foreground">Card</span>, and <span className="font-semibold text-foreground">E-transfer</span>.<br />
                      E-transfer to <span className="font-semibold text-foreground">xavier.robichaud318@gmail.com</span>.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {stepKey === "done" && confirmed && (
            <motion.div key="s3" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }} className="max-w-lg mx-auto text-center py-10" data-testid="confirmation-screen">
              <span className="grid place-items-center w-16 h-16 rounded-full bg-primary text-primary-foreground mx-auto mb-6"><CheckCircle2 className="w-8 h-8" /></span>
              <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl">{confirmed.no_appointment ? "Request received!" : "You're booked!"}</h1>
              <p className="text-muted-foreground mt-3">{confirmed.no_appointment ? <>Thanks! I'll reach out to <span className="font-semibold text-foreground">{confirmed.customer.email}</span> to arrange a time.</> : <>A confirmation has been sent to <span className="font-semibold text-foreground">{confirmed.customer.email}</span>.</>}</p>
              <div className="bg-card border border-border rounded p-6 mt-8 text-left space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Service</span><span className="font-semibold">{confirmed.service_name}</span></div>
                {confirmed.addon_names?.length > 0 && (<div className="flex justify-between"><span className="text-muted-foreground">Add-ons</span><span className="font-semibold">{confirmed.addon_names.join(", ")}</span></div>)}
                {confirmed.duration_minutes && (<div className="flex justify-between"><span className="text-muted-foreground">Duration</span><span className="font-semibold">{confirmed.duration_minutes} min</span></div>)}
                {confirmed.workers && (<div className="flex justify-between"><span className="text-muted-foreground">Crew</span><span className="font-semibold">{confirmed.workers} {confirmed.workers === 1 ? "person" : "people"}</span></div>)}
                {confirmed.no_appointment ? (
                  <div className="flex justify-between"><span className="text-muted-foreground">Scheduling</span><span className="font-semibold">I'll be in touch</span></div>
                ) : (
                  <>
                    <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span className="font-semibold">{prettyDate(confirmed.date)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Time</span><span className="font-semibold">{confirmed.time}</span></div>
                  </>
                )}
                <div className="flex justify-between border-t border-border pt-3"><span className="text-muted-foreground">Total</span><span className="font-display font-bold text-primary">{confirmed.total_price != null ? `$${confirmed.total_price}` : "Custom quote"}</span></div>
              </div>
              <div className="bg-secondary rounded p-4 mt-4 text-left" data-testid="confirm-payment-methods">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-primary"><CreditCard className="w-3.5 h-3.5" /> How to pay (after the job)</div>
                <p className="text-sm text-muted-foreground mt-1.5">Cash, Card, or E-transfer to <span className="font-semibold text-foreground">xavier.robichaud318@gmail.com</span>.</p>
              </div>
              <Link to="/" data-testid="confirm-home-button" className="inline-flex items-center gap-2 mt-8 bg-primary text-primary-foreground font-bold px-7 py-3.5 rounded transition-transform active:scale-95 hover:bg-primary/90">Back to home</Link>
            </motion.div>
          )}
        </AnimatePresence>

        {stepKey !== "done" && (
          <div className="flex items-center justify-between mt-12 pt-6 border-t border-border">
            <button data-testid="booking-back-button" onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))} className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {stepKey !== "details" ? (
              <button data-testid="booking-next-button" disabled={!canNext()} onClick={() => setStep(step + 1)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-7 py-3 rounded transition-transform active:scale-95 hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button data-testid="booking-submit-button" disabled={!canNext() || submitting} onClick={submit} className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-7 py-3 rounded transition-transform active:scale-95 hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed">
                {submitting ? "Booking..." : (service?.no_appointment ? "Send request" : "Confirm booking")} <Check className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
